import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { Menu, Phone, User, LogOut, LayoutDashboard, FileText } from "lucide-react";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  // Full nav link list shown directly in the desktop row (no "More" dropdown) —
  // kept tight (small gaps/padding) so the whole bar fits from lg (1024px, iPad landscape) upward.
  const navLinks = [
    { href: "/services", label: "Services" },
    { href: "/project-payment-management", label: "Project Payments" },
    { href: "/labour-registry", label: "Labour Registry" },
    { href: "/portfolio", label: "Portfolio" },
    { href: "/quote", label: "Free Quote" },
    { href: "/booking", label: "Book Now" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 navbar-glass border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-5 xl:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="navbar-logo">
            <img 
              src="/protocall-logo.png" 
              alt="ProToCall" 
              className="h-11 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation — visible from lg (tablet landscape, e.g. iPad) upward */}
          <nav className="hidden lg:flex items-center gap-3 xl:gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-500 ${
                  isActive(link.href) ? "text-orange-500" : "text-slate-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/pay-invoice"
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-500 ${
                isActive("/pay-invoice") ? "text-orange-500" : "text-slate-600"
              }`}
              data-testid="nav-link-pay-invoice"
            >
              Pay Invoice
            </Link>
          </nav>

          {/* CTA Buttons — visible from lg upward; phone number only reappears at xl+ once there's more room */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-4 pl-4 xl:pl-5">
            <a
              href="tel:+17587230202"
              className="hidden xl:inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors whitespace-nowrap"
              data-testid="navbar-phone"
            >
              <span>+17587230202</span>
            </a>
            <span className="hidden xl:inline h-5 w-px bg-slate-200" aria-hidden="true" />

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 border-slate-300"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-4 h-4" />
                    <span className="max-w-[100px] truncate">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/pay-invoice" className="flex items-center gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" />
                      Pay Invoice
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="flex items-center gap-2 cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                asChild
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 xl:px-6 h-10 rounded-md"
                data-testid="navbar-login-btn"
              >
                <Link to="/login">Sign In</Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu — only for portrait/mobile widths below lg (1024px) */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full max-w-sm overflow-y-auto rounded-bl-2xl border-b"
              style={{ top: 0, bottom: "auto", height: "auto", maxHeight: "85vh" }}
            >
              <div className="flex flex-col">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <img 
                    src="/protocall-logo.png" 
                    alt="ProToCall" 
                    className="h-9 w-auto"
                  />
                </div>
                <nav className="flex flex-col mt-2">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`text-base font-medium py-1.5 transition-colors hover:text-orange-500 ${
                        isActive(link.href) ? "text-orange-500" : "text-slate-600"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/pay-invoice"
                    onClick={() => setIsOpen(false)}
                    className="text-base font-medium py-1.5 text-slate-600 hover:text-orange-500"
                  >
                    Pay Invoice
                  </Link>
                  {isAuthenticated && (
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="text-base font-medium py-1.5 text-slate-600 hover:text-orange-500"
                    >
                      Dashboard
                    </Link>
                  )}
                </nav>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <a
                    href="tel:+17587230202"
                    className="flex items-center gap-2 text-sm text-slate-600 mb-3"
                  >
                    <Phone className="w-4 h-4" />
                    <span>+17587230202</span>
                  </a>
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <p className="text-sm text-slate-500">Signed in as {user?.email}</p>
                      <Button
                        onClick={() => {
                          logout();
                          setIsOpen(false);
                        }}
                        variant="outline"
                        className="w-full border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="mr-2 w-4 h-4" />
                        Sign Out
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        asChild
                        variant="outline"
                        className="w-full"
                      >
                        <Link to="/login" onClick={() => setIsOpen(false)}>
                          Sign In
                        </Link>
                      </Button>
                      <Button
                        asChild
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11 rounded-md"
                      >
                        <Link to="/register" onClick={() => setIsOpen(false)}>
                          Get Started
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
